import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import i18n from '@/i18n';
import { useNotificationCenter } from 'react-toastify/addons/use-notification-center';
import { doGet } from '@modules/DataManager';
import { getGlobalState } from '@modules/utils';
import { eventsMap } from '@modules/logic/eventsLogic';
import { findByIdOrAccountId } from '@modules/logic/driversLogic';
import {
  setNotificationCenterInstance,
  loadNotificationsFromStorage,
  saveNotificationsToStorage,
  loadDismissedNotificationIds,
  addDismissedNotificationId,
  addDismissedNotificationIds,
} from '@modules/services/notificationDispatcher';
import './DashboardNotificationCenter.css';
import { Icon } from '@ui';

const PAGE_SIZE = 50;
const MAX_REHYDRATE = 300;
const UPCOMING_DAYS = 3;

function toMs(d) {
  if (!d) return 0;
  if (d?.seconds) return d.seconds * 1000;
  if (d?._seconds) return d._seconds * 1000;
  if (typeof d === 'number') return d;
  const ms = new Date(d).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function getEventDateMsFromNotification(n) {
  return toMs(n?.data?.eventDateMs) || toMs(n?.data?.eventDate) || toMs(n?.createdAt);
}

function getWarningDateMsFromNotification(n) {
  return toMs(n?.data?.warningDateMs) || toMs(n?.createdAt);
}

function isDriverWarning(n) {
  return n?.data?.kind === 'driver_warning';
}

function isOrder(n) {
  return n?.data?.kind === 'order';
}

function isUpcomingEvent(n) {
  if (n?.data?.kind !== 'event') return false;
  const eventMs = getEventDateMsFromNotification(n);
  if (!eventMs) return false;

  const now = dayjs();
  const end = dayjs().add(UPCOMING_DAYS, 'days').endOf('day');
  return dayjs(eventMs).isBetween(now, end, null, '[]');
}

export default function DashboardNotificationCenter() {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [rehydrating, setRehydrating] = useState(false);
  const [rehydratedOnce, setRehydratedOnce] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // "all", "warnings", "orders", "events", "scadenze"
  const rootRef = useRef(null);

  // dedup runtime
  const addedIdsRef = useRef(new Set());

  // dismissed ids (persistenti)
  const dismissedIdsRef = useRef(new Set());
  useMemo(() => {
    const ids = loadDismissedNotificationIds();
    dismissedIdsRef.current = new Set(ids);
    return null;
  }, []);

  // Load saved notifications (ma escludi quelle dismissate)
  const savedNotifications = useMemo(() => {
    const stored = loadNotificationsFromStorage();
    return (stored || []).filter((n) => !dismissedIdsRef.current.has(n?.id));
  }, []);

  const { notifications, unreadCount, markAsRead, markAllAsRead, remove, clear, add, find } =
    useNotificationCenter({
      data: savedNotifications,
      filter: (n) => {
        if (n.containerId !== 'dashboard') return false;

        // escludi course (solo sugli eventi)
        const isCourseEvent = n.data?.kind === 'event' && n.data?.eventType === 'course';
        if (isCourseEvent) return false;

        // escludi flyering (solo sugli eventi)
        const isFlyeringEvent = n.data?.kind === 'event' && n.data?.eventType === 'flyering';
        if (isFlyeringEvent) return false;

        // escludi notifiche dismissate
        if (dismissedIdsRef.current.has(n.id)) return false;

        return true;
      },

      // ✅ ordinamento:
      // 1) non letti sempre in cima
      // 2) tra i non letti: eventi nei prossimi 3 giorni prima, poi per data
      // 3) tra i letti: stessi criteri ma dopo
      sort: (a, b) => {
        const aUnread = !a.read;
        const bUnread = !b.read;

        if (aUnread && !bUnread) return -1;
        if (!aUnread && bUnread) return 1;

        const aUp = isUpcomingEvent(a);
        const bUp = isUpcomingEvent(b);

        if (aUp && !bUp) return -1;
        if (!aUp && bUp) return 1;

        if (aUp && bUp) {
          const aEvent = getEventDateMsFromNotification(a);
          const bEvent = getEventDateMsFromNotification(b);
          return aEvent - bEvent;
        }

        // Priorità 4: ordini in cima tra i non letti
        const aOrder = isOrder(a);
        const bOrder = isOrder(b);

        if (aUnread && bUnread) {
          if (aOrder && !bOrder) return -1;
          if (!aOrder && bOrder) return 1;
        }

        // Priorità 5: fallback per data (warningDateMs o createdAt) desc
        const aMs = isDriverWarning(a) ? getWarningDateMsFromNotification(a) : a.createdAt || 0;
        const bMs = isDriverWarning(b) ? getWarningDateMsFromNotification(b) : b.createdAt || 0;
        return bMs - aMs;
      },
    });

  const visible = useMemo(() => {
    let filtered = notifications || [];

    if (activeTab === 'warnings') {
      filtered = filtered.filter((n) => isDriverWarning(n));
    } else if (activeTab === 'orders') {
      filtered = filtered.filter((n) => isOrder(n));
    } else if (activeTab === 'events') {
      filtered = filtered.filter((n) => n?.data?.kind === 'event');
    } else if (activeTab === 'scadenze') {
      filtered = filtered.filter((n) => n?.data?.kind === 'scadenza');
    }

    return filtered.slice(0, visibleCount);
  }, [notifications, visibleCount, activeTab]);

  // Register notification center instance with global dispatcher
  useEffect(() => {
    setNotificationCenterInstance({
      add,
      find,
      remove,
      markAsRead,
      markAllAsRead,
      clear,
      notifications,
    });
  }, [add, find, remove, markAsRead, markAllAsRead, clear, notifications]);

  // ✅ Save notifications to localStorage whenever they change (anche [])
  useEffect(() => {
    saveNotificationsToStorage(notifications || []);
  }, [notifications]);

  // chiudi cliccando fuori / ESC
  useEffect(() => {
    if (!open) return;

    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Rehydrate: quando apri la campanella carica events + driver warnings
  useEffect(() => {
    if (!open) return;
    if (rehydratedOnce) return;

    const load = async () => {
      setRehydrating(true);

      try {
        const state = getGlobalState();
        const managerId = state.user?.gestore || state.user?.id_gestore;
        if (!managerId) return;

        const cutoffMs = dayjs().subtract(7, 'days').startOf('day').valueOf();

        const [eventsRes, warningsRes] = await Promise.all([
          doGet('/v2/fleet/events/get-events-by-manager', { manager_id: managerId }),
          doGet('/v2/fleet/driver-warnings/get-driver-warnings-by-manager', {
            manager_id: managerId,
          }),
        ]);

        const events = Array.isArray(eventsRes) ? eventsRes : [];
        const warnings = Array.isArray(warningsRes) ? warningsRes : [];

        const filteredEvents = events
          .filter((e) => !e?.deleted)
          .filter((e) => e?.type !== 'course')
          .filter((e) => e?.type !== 'flyering')
          .filter((e) => {
            const ms = toMs(e?.date);
            if (!ms) return false;
            if (ms > Date.now()) return true;
            return ms >= cutoffMs;
          })
          .sort((a, b) => toMs(b?.date) - toMs(a?.date))
          .slice(0, MAX_REHYDRATE);

        const filteredWarnings = warnings
          .filter((w) => {
            const ms = toMs(w?.data_segnalazione);
            if (!ms) return false;
            if (ms > Date.now()) return true;
            return ms >= cutoffMs;
          })
          .sort((a, b) => toMs(b?.data_segnalazione) - toMs(a?.data_segnalazione))
          .slice(0, MAX_REHYDRATE);

        const existingIds = new Set((notifications || []).map((n) => n.id));

        // eventi
        filteredEvents.forEach((ev) => {
          const id = `event-${ev.id}`;
          const eventDateMs = toMs(ev?.date) || Date.now();

          // ✅ filtro per status
          if (ev.status !== 'completed' && ev.status !== 'viewed') return;

          // ✅ se dismissato non riaggiungere mai
          if (dismissedIdsRef.current.has(id)) return;

          if (existingIds.has(id)) return;
          if (addedIdsRef.current.has(id)) return;
          if (typeof find === 'function' && find(id)) return;

          addedIdsRef.current.add(id);

          // Risoluzione nomi
          const state = getGlobalState();
          const driver = findByIdOrAccountId(state.drivers, ev.driver_id);
          const vehicle = state.vehicles?.find((v) => v.id === ev.vehicle_id);

          const driverName = driver
            ? `${driver.nome || ''} ${driver.cognome || ''}`.trim()
            : ev.driver_id || null;
          const vehicleLabel = vehicle
            ? vehicle.targa || vehicle.modello || ev.vehicle_id
            : ev.vehicle_id || null;

          // Title & Text Generation
          const typeKey = ev.type || 'unknown';

          let statusTitleKey = 'evento';
          let statusMsgKey = '';

          if (ev.status === 'viewed') {
            statusTitleKey = 'presa_visione';
            statusMsgKey = 'msg_presa_visione';
          } else if (ev.status === 'completed') {
            statusTitleKey = 'evento_completato';
            statusMsgKey = 'msg_evento_completato';
          }

          const title = `${i18n.t(statusTitleKey)}: ${i18n.t(typeKey)}`;

          const addresses =
            `${ev.start_address || ''}${ev.end_address ? ' → ' + ev.end_address : ''}`.trim();
          const notes = ev.note || ev.description || '';
          const textPrefix = statusMsgKey ? i18n.t(statusMsgKey) : '';

          const text = [textPrefix, addresses, notes].filter(Boolean).join('. ');

          add({
            id,
            createdAt: eventDateMs,
            read: false,
            containerId: 'dashboard',
            data: {
              kind: 'event',
              eventType: typeKey,
              eventDateMs,
              title,
              text,
              eventId: ev.id,
              status: ev.status,
              image: ev.image || null,
              coords: ev.coords || null,
              driverName,
              vehicleLabel,
            },
          });
        });

        // driver warnings
        filteredWarnings.forEach((w) => {
          const dateMs = toMs(w?.data_segnalazione) || Date.now();
          const id = `warning-${w.id || w.id_driver}-${dateMs}`;

          // ✅ se dismissato non riaggiungere mai
          if (dismissedIdsRef.current.has(id)) return;

          if (existingIds.has(id)) return;
          if (addedIdsRef.current.has(id)) return;
          if (typeof find === 'function' && find(id)) return;

          addedIdsRef.current.add(id);

          add({
            id,
            createdAt: dateMs,
            read: false,
            containerId: 'dashboard',
            data: {
              kind: 'driver_warning',
              warningDateMs: dateMs,
              title: `${i18n.t('driver_warning')}: ${w.motivo || i18n.t('segnalazione')}`,
              text: w.commento || i18n.t('nessun_commento'),
              driverId: w.id_driver,
              warningId: w.id,
            },
          });
        });

        setRehydratedOnce(true);
      } catch (e) {
        console.error('NotificationCenter rehydrate error:', e);
      } finally {
        setRehydrating(false);
      }
    };

    load();
  }, [open, rehydratedOnce, add, find, notifications]);

  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next) setVisibleCount(PAGE_SIZE);
      return next;
    });
  };

  const counts = useMemo(() => {
    const list = notifications || [];
    return {
      all: list.length,
      warnings: list.filter((n) => isDriverWarning(n)).length,
      orders: list.filter((n) => isOrder(n)).length,
      events: list.filter((n) => n?.data?.kind === 'event').length,
      scadenze: list.filter((n) => n?.data?.kind === 'scadenza').length,
    };
  }, [notifications]);

  return (
    <div className="notifCenter mr-2" ref={rootRef}>
      <button
        type="button"
        className="notifBellBtn"
        onClick={toggleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Notifiche"
      >
        <Icon name="notification" />
        {unreadCount > 0 ? <span className="notifBadge">{unreadCount}</span> : null}
      </button>

      {open && (
        <div className="notifPanel">
          <div className="notifHeader">
            <strong>Notifiche</strong>
            <div className="notifActions">
              <button type="button" className="notifLink" onClick={() => markAllAsRead(true)}>
                Segna tutte lette
              </button>

              <button
                type="button"
                className="notifLink"
                onClick={() => {
                  // ✅ svuota = nascondi tutto in modo persistente
                  const idsToDismiss = (notifications || []).map((n) => n.id);
                  addDismissedNotificationIds(idsToDismiss);
                  idsToDismiss.forEach((id) => dismissedIdsRef.current.add(id));

                  clear();
                  saveNotificationsToStorage([]);
                  setVisibleCount(PAGE_SIZE);
                  setRehydratedOnce(false);
                  addedIdsRef.current = new Set();
                }}
              >
                Svuota
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="notifTabs">
            <button
              className={`notifTab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              Tutte ({counts.all})
            </button>

            <button
              className={`notifTab ${activeTab === 'warnings' ? 'active' : ''}`}
              onClick={() => setActiveTab('warnings')}
            >
              Avvisi Guidatori ({counts.warnings})
            </button>

            <button
              className={`notifTab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Ordini ({counts.orders})
            </button>

            <button
              className={`notifTab ${activeTab === 'events' ? 'active' : ''}`}
              onClick={() => setActiveTab('events')}
            >
              Eventi ({counts.events})
            </button>

            <button
              className={`notifTab ${activeTab === 'scadenze' ? 'active' : ''}`}
              onClick={() => setActiveTab('scadenze')}
            >
              Scadenze ({counts.scadenze})
            </button>
          </div>

          {rehydrating ? (
            <div className="notifEmpty">Caricamento…</div>
          ) : visible.length === 0 ? (
            <div className="notifEmpty">
              {activeTab === 'warnings'
                ? 'Nessun avviso per guidatori'
                : activeTab === 'orders'
                  ? 'Nessun ordine'
                  : activeTab === 'events'
                    ? 'Nessun evento'
                    : activeTab === 'scadenze'
                      ? 'Nessuna scadenza'
                      : 'Nessuna notifica'}
            </div>
          ) : (
            <>
              <ul className="notifList">
                {visible.map((n) => {
                  const upcoming = isUpcomingEvent(n);
                  const order = isOrder(n);
                  const eventDateMs = getEventDateMsFromNotification(n);

                  let icon = null;
                  if (n.data?.kind === 'scadenza') {
                    if (n.data?.urgencyLevel === 'expired') {
                      icon = (
                        <Icon
                          name="calendarTimes"
                          style={{ color: '#dc3545', marginRight: '6px' }}
                        />
                      );
                    } else if (n.data?.urgencyLevel === 'urgent') {
                      icon = (
                        <Icon name="warning" style={{ color: '#ffc107', marginRight: '6px' }} />
                      );
                    } else {
                      icon = (
                        <Icon
                          name="calendarDays"
                          style={{ color: '#ffc107', marginRight: '6px' }}
                        />
                      );
                    }
                  } else if (n.data?.kind === 'driver_warning') {
                    icon = <Icon name="error" style={{ color: '#fd7e14', marginRight: '6px' }} />;
                  } else if (n.data?.kind === 'event') {
                    const evType = n.data?.eventType;
                    const mapItem = eventsMap[evType] || eventsMap['other'];
                    if (mapItem) {
                      icon = (
                        <i
                          className={`fa-solid fa-${mapItem.icon}`}
                          style={{ color: mapItem.color, marginRight: '6px' }}
                        ></i>
                      );
                    }
                  } else if (order) {
                    if (n.data?.orderType === 'order_created') {
                      icon = (
                        <Icon name="addToCart" style={{ color: '#28a745', marginRight: '6px' }} />
                      );
                    } else if (n.data?.orderType === 'order_canceled') {
                      icon = <Icon name="ban" style={{ color: '#dc3545', marginRight: '6px' }} />;
                    } else {
                      icon = <Icon name="cart" style={{ marginRight: '6px' }} />;
                    }
                  }

                  return (
                    <li
                      key={n.id}
                      className={`notifItem ${n.read ? 'isRead' : 'isUnread'} ${
                        upcoming ? 'isUpcoming' : ''
                      } ${order ? 'isOrder' : ''}`}
                      onClick={() => markAsRead(n.id)}
                    >
                      <div className="notifItemRow">
                        <div className="notifText">
                          <div className="notifTitle">
                            {upcoming ? <Icon name="pin" style={{ marginRight: '6px' }} /> : null}
                            {icon}
                            {n.data?.title ??
                              (typeof n.content === 'string' ? n.content : 'Notifica')}
                            {upcoming ? (
                              <span className="notifUpcomingTag">Entro 3 giorni</span>
                            ) : null}
                          </div>

                          {n.data?.kind !== 'event' && n.data?.text ? (
                            <div className="notifSub">{n.data.text}</div>
                          ) : null}

                          {n.data?.kind === 'event' ? (
                            <div className="notifSub muted">
                              {/* Explicitly render text for events here */}
                              {n.data?.text && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: 'var(--text-primary, #333)',
                                  }}
                                >
                                  {n.data.text}
                                </div>
                              )}

                              <div>
                                {i18n.t('evento')}:{' '}
                                {n.data?.eventType
                                  ? i18n.t(n.data.eventType)
                                  : i18n.t('sconosciuto')}
                                {eventDateMs
                                  ? ` • ${dayjs(eventDateMs).format('DD/MM HH:mm')}`
                                  : ''}
                              </div>

                              {/* Driver e Veicolo */}
                              {(n.data?.driverName || n.data?.vehicleLabel) && (
                                <div style={{ fontSize: '0.9em', marginTop: '2px', color: '#666' }}>
                                  {[
                                    n.data.driverName
                                      ? `${i18n.t('driver')}: ${n.data.driverName}`
                                      : null,
                                    n.data.vehicleLabel
                                      ? `${i18n.t('veicolo')}: ${n.data.vehicleLabel}`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' • ')}
                                </div>
                              )}

                              {/* Immagine */}
                              {n.data.image && (
                                <div style={{ marginTop: '5px' }}>
                                  <img
                                    src={n.data.image}
                                    alt="Allegato"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '150px',
                                      borderRadius: '4px',
                                    }}
                                  />
                                </div>
                              )}

                              {/* Coordinate */}
                              {n.data.coords && (
                                <div style={{ marginTop: '5px' }}>
                                  <Icon name="location" style={{ marginRight: '5px' }} />
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${n.data.coords.lat},${n.data.coords.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                  >
                                    {i18n.t('vedi_mappa')}
                                  </a>
                                </div>
                              )}
                            </div>
                          ) : null}

                          {n.data?.kind === 'order' ? (
                            <div className="notifSub muted">
                              {i18n.t('ordine')}{' '}
                              {n.data?.orderType === 'order_created'
                                ? i18n.t('creato')
                                : i18n.t('annullato')}
                              {n.data?.executionTime ? ` • ${n.data.executionTime}` : ''}
                            </div>
                          ) : null}

                          {n.data?.kind === 'driver_warning' ? (
                            <div className="notifSub muted">
                              Warning • Driver: {n.data?.driverName || n.data?.driverId || 'N/D'}
                              {n.data?.warningDateMs
                                ? ` • ${dayjs(n.data.warningDateMs).format('DD/MM HH:mm')}`
                                : ''}
                            </div>
                          ) : null}

                          {n.data?.kind === 'scadenza' ? (
                            <div className="notifSub muted">
                              {n.data?.scadenzaType === 'invoice' ? (
                                <>
                                  {n.data?.label || 'N/D'}
                                  {n.data?.expirationDate
                                    ? ` • Scadenza: ${dayjs(n.data.expirationDate).format('DD/MM/YYYY')}`
                                    : ''}
                                </>
                              ) : (
                                <>
                                  {i18n.t('veicolo')}:{' '}
                                  {n.data?.vehiclePlate || n.data?.vehicleLabel || 'N/D'}
                                  {n.data?.expirationDate
                                    ? ` • ${i18n.t('scadenza_label')}: ${dayjs(n.data.expirationDate).format('DD/MM/YYYY')}`
                                    : ''}
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>

                        <div className="notifMeta">
                          <span className="notifTime">{dayjs(n.createdAt).fromNow()}</span>
                          <button
                            type="button"
                            className="notifRemove"
                            onClick={(e) => {
                              e.stopPropagation();

                              // ✅ persist dismiss
                              addDismissedNotificationId(n.id);
                              dismissedIdsRef.current.add(n.id);

                              // ✅ rimuovi dallo stato
                              remove(n.id);

                              // ✅ salva subito storage senza la notifica
                              const next = (notifications || []).filter((x) => x.id !== n.id);
                              saveNotificationsToStorage(next);
                            }}
                            aria-label="Rimuovi notifica"
                            title="Rimuovi"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {visibleCount < (notifications || []).length && (
                <div className="notifFooter">
                  <button
                    type="button"
                    className="notifLoadMore"
                    onClick={() =>
                      setVisibleCount((c) => Math.min(c + PAGE_SIZE, (notifications || []).length))
                    }
                  >
                    Carica altre ({Math.min(PAGE_SIZE, (notifications || []).length - visibleCount)}
                    )
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
